import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API, BACKEND_URL } from "../App";
import { toast } from "sonner";
import { 
  Home, LogOut, Briefcase, CreditCard, User, Menu, X,
  MapPin, Calendar, DollarSign, Phone, Mail, Eye, Check, Star,
  Shield, Clock, Wrench, Building2, MessageSquare, Award,
  Unlock, Coins, Zap, Crown, Lock, AlertTriangle, CheckCircle,
  ArrowRight, ExternalLink, Plus, Package, BadgeCheck, TrendingUp,
  Info, BarChart3, Archive, Beaker, Gift, Camera
} from "lucide-react";
import { LeadPhotoGallery } from "../components/LeadPhotoGallery";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import SubscriptionStatusBanner from "../components/SubscriptionStatusBanner";
import LeadsFilterBar from "../components/LeadsFilterBar";
import { BadgeRow } from "../components/ContractorBadges";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import ContractorRatingsSection from "../components/ContractorRatingsSection";

// Subscription plan styling
const PLAN_STYLES = {
  starter: { color: "bg-blue-500", icon: Zap, badge: "bg-blue-100 text-blue-800" },
  pro: { color: "bg-purple-500", icon: Star, badge: "bg-purple-100 text-purple-800" },
  elite: { color: "bg-amber-500", icon: Crown, badge: "bg-amber-100 text-amber-800" }
};

// Credit Pack styling
const PACK_STYLES = {
  pack_10: { color: "bg-emerald-500", popular: false },
  pack_25: { color: "bg-blue-500", popular: true },
  pack_50: { color: "bg-purple-500", popular: false }
};

// ---------- Unlock-status styling helpers (extracted from nested ternaries) ----------
const UNLOCK_STATUS_BADGE_CLASS = {
  won: 'bg-green-100 text-green-800',
  lost: 'bg-red-100 text-red-800',
  contracted: 'bg-blue-100 text-blue-800',
};
const UNLOCK_STATUS_LABEL = {
  new: 'New',
  contracted: 'Contracted',
  won: 'Won',
  lost: 'Lost',
};
const getUnlockBadgeClass = (status) =>
  UNLOCK_STATUS_BADGE_CLASS[status] || 'bg-amber-100 text-amber-800';
const getUnlockLabel = (status) => UNLOCK_STATUS_LABEL[status] || status;

// ---------- Profile-completeness helpers ----------
const getCompletenessColor = (pct) => {
  if (pct >= 80) return '#7ED957';
  if (pct >= 50) return '#0FA3B1';
  return '#F59E0B';
};
const getCompletenessText = (pct) => {
  if (pct < 50) return 'Just getting started';
  if (pct < 80) return 'Good progress!';
  return 'Excellent!';
};

export default function ContractorDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("leads");
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  const [creditPacks, setCreditPacks] = useState({});
  const [availableLeads, setAvailableLeads] = useState([]);
  const [myLeads, setMyLeads] = useState([]);
  const [leadsSubTab, setLeadsSubTab] = useState("active"); // "active" or "archived"
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [unlockingLead, setUnlockingLead] = useState(false);
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [subscribingPlan, setSubscribingPlan] = useState(null);
  const [showCreditPackDialog, setShowCreditPackDialog] = useState(false);
  const [purchasingPack, setPurchasingPack] = useState(null);
  // Subscription status state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  // Job completion flow states
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completingJob, setCompletingJob] = useState(false);
  const [selectedJobLead, setSelectedJobLead] = useState(null);
  // Finished flow states
  const [showFinishDialog, setShowFinishDialog] = useState(false);
  const [finishingJob, setFinishingJob] = useState(false);
  const [showUndoFinishDialog, setShowUndoFinishDialog] = useState(false);
  // Lead filter states
  const [filterPreferences, setFilterPreferences] = useState({ saved_preferences: {}, profile_defaults: {} });
  const [currentFilters, setCurrentFilters] = useState({ job_sizes: [], job_types: [], sort_by: "newest" });
  const [leadsLoading, setLeadsLoading] = useState(false);

  useEffect(() => {
    checkOnboardingAndFetchData();
    fetchSubscriptionStatus();
    
    // Handle Stripe return
    const subscriptionStatusParam = searchParams.get('subscription');
    const creditStatus = searchParams.get('credits');
    const sessionId = searchParams.get('session_id');
    
    if ((subscriptionStatusParam === 'success' || creditStatus === 'success') && sessionId) {
      handlePaymentSuccess(sessionId);
    } else if (subscriptionStatusParam === 'cancelled') {
      toast.error("Subscription cancelled");
    } else if (creditStatus === 'cancelled') {
      toast.error("Credit purchase cancelled");
    }
    
    // Check for trial start param
    const startTrial = searchParams.get('start_trial');
    if (startTrial === 'true') {
      handleStartTrial();
    }
  }, []);
  
  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/contractor/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      /* silent - error deliberately ignored */
    }
  };
  
  const handleStartTrial = async () => {
    try {
      const response = await axios.post(`${API}/contractor/start-trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Pro trial started! You have 7 days of full Pro access with 25 credits.");
      fetchSubscriptionStatus();
      fetchData();
      // Navigate to Available Leads after starting trial
      setActiveTab("leads");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start trial");
    }
  };

  const handlePaymentSuccess = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.payment_status === 'paid') {
        toast.success("Payment successful! Your account has been updated.");
        await fetchData();
        fetchSubscriptionStatus();
      }
    } catch (error) {
      /* silent - error deliberately ignored */
    }
    
    // Clean up URL
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const checkOnboardingAndFetchData = async () => {
    try {
      // First check onboarding status
      const onboardingRes = await axios.get(`${API}/contractor/onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If not onboarded, redirect to onboarding
      if (!onboardingRes.data.onboarding_complete) {
        navigate("/contractor/onboarding");
        return;
      }
      
      await fetchData();
    } catch (error) {
      // If 404 on onboarding check, might need to redirect
      if (error.response?.status === 404) {
        navigate("/contractor/onboarding");
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [profileRes, subscriptionRes, plansRes, packsRes, filterPrefsRes, myLeadsRes] = await Promise.all([
        axios.get(`${API}/contractor/full-profile`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/contractor/subscription`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/subscription/plans`),
        axios.get(`${API}/credit-packs`),
        axios.get(`${API}/contractor/lead-filter-preferences`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { saved_preferences: {}, profile_defaults: {} } })),
        axios.get(`${API}/contractor/my-leads`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      setProfile(profileRes.data);
      setSubscription(subscriptionRes.data);
      setSubscriptionPlans(plansRes.data);
      setCreditPacks(packsRes.data);
      setFilterPreferences(filterPrefsRes.data);
      setMyLeads(myLeadsRes.data);
      
      // Leads will be fetched by the filter bar component after initialization
    } catch (error) {
      /* silent - error deliberately ignored */
    }
  };

  // Fetch leads with filters (called by filter bar)
  const fetchLeadsWithFilters = useCallback(async (filters) => {
    setLeadsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.job_sizes?.length > 0) {
        params.append('job_sizes', filters.job_sizes.join(','));
      }
      if (filters.job_types?.length > 0) {
        params.append('job_types', filters.job_types.join(','));
      }
      if (filters.sort_by) {
        params.append('sort_by', filters.sort_by);
      }
      
      const url = `${API}/leads/available${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setAvailableLeads(response.data);
      setCurrentFilters(filters);
    } catch (error) {
      setAvailableLeads([]);
    } finally {
      setLeadsLoading(false);
    }
  }, [token]);

  const handleFilterChange = useCallback((filters) => {
    fetchLeadsWithFilters(filters);
  }, [fetchLeadsWithFilters]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSubscribe = async (planKey) => {
    setSubscribingPlan(planKey);
    try {
      const response = await axios.post(`${API}/contractor/subscription/checkout`, 
        { 
          plan: planKey,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Subscription failed");
      setSubscribingPlan(null);
    }
  };

  const handleBuyCreditPack = async (packId) => {
    setPurchasingPack(packId);
    try {
      const response = await axios.post(`${API}/contractor/credits/checkout`, 
        { 
          pack_id: packId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Purchase failed");
      setPurchasingPack(null);
    }
  };

  const handleUnlockLead = async () => {
    if (!selectedLead) return;
    
    setUnlockingLead(true);
    try {
      const response = await axios.post(`${API}/leads/unlock`, 
        { lead_id: selectedLead.lead_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Lead unlocked! Contact info is now available.");
      setShowUnlockDialog(false);
      setSelectedLead(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unlock lead");
    } finally {
      setUnlockingLead(false);
    }
  };

  const openUnlockDialog = (lead) => {
    if (subscription?.subscription_status !== 'active') {
      toast.error("Please subscribe first to unlock leads");
      setActiveTab("subscription");
      return;
    }
    
    // Skip credit check for unlimited credits users (founder promo)
    if (!subscription?.is_unlimited_credits) {
      const creditBalance = subscription?.credit_balance || 0;
      if (creditBalance < lead.credit_cost) {
        toast.error(`Not enough credits. You need ${lead.credit_cost} credits, but have ${creditBalance}.`);
        return;
      }
    }
    
    setSelectedLead(lead);
    setShowUnlockDialog(true);
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/contractor/my-leads/${leadId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lead status updated");
      await fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Mark job as complete - triggers homeowner SMS confirmation
  const handleMarkJobComplete = (lead) => {
    setSelectedJobLead(lead);
    setShowCompleteDialog(true);
  };

  const confirmMarkJobComplete = async () => {
    if (!selectedJobLead) return;
    
    setCompletingJob(true);
    try {
      const response = await axios.post(
        `${API}/contractor/jobs/${selectedJobLead.lead_id}/mark-complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || "Job marked complete! Homeowner will be notified.");
      setShowCompleteDialog(false);
      setSelectedJobLead(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark job complete");
    } finally {
      setCompletingJob(false);
    }
  };

  // Handle "Finished" button click - opens confirmation dialog
  const handleFinishJob = (lead) => {
    setSelectedJobLead(lead);
    setShowFinishDialog(true);
  };

  // Confirm marking job as finished - archives and sends rating SMS
  const confirmFinishJob = async () => {
    if (!selectedJobLead) return;
    
    setFinishingJob(true);
    try {
      const response = await axios.post(
        `${API}/contractor/jobs/${selectedJobLead.lead_id}/finish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || "Job finished! Homeowner has been texted to rate you.");
      setShowFinishDialog(false);
      setSelectedJobLead(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to finish job");
    } finally {
      setFinishingJob(false);
    }
  };

  // Handle "Undo Finished" button click - opens confirmation dialog
  const handleUndoFinish = (lead) => {
    setSelectedJobLead(lead);
    setShowUndoFinishDialog(true);
  };

  // Confirm undoing job completion - returns to contracted status
  const confirmUndoFinish = async () => {
    if (!selectedJobLead) return;
    
    setFinishingJob(true);
    try {
      const response = await axios.post(
        `${API}/contractor/jobs/${selectedJobLead.lead_id}/undo-finish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || "Job returned to Active leads.");
      setShowUndoFinishDialog(false);
      setSelectedJobLead(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to undo completion");
    } finally {
      setFinishingJob(false);
    }
  };

  // Get job status display info
  const getJobStatusInfo = (lead) => {
    const unlockStatus = lead.unlock_status || lead.status || "new";
    const jobStatus = lead.job_status || "in_progress";
    
    // Handle unlock_status-based statuses first
    if (unlockStatus === "completed") {
      return { label: "Completed", color: "bg-green-100 text-green-800", canComplete: false, canFinish: false, canUndoFinish: !lead.rating_submitted_at };
    }
    if (unlockStatus === "contracted") {
      return { label: "Contracted", color: "bg-blue-100 text-blue-800", canComplete: true, canFinish: true, canUndoFinish: false };
    }
    if (unlockStatus === "lost") {
      return { label: "Lost", color: "bg-red-100 text-red-800", canComplete: false, canFinish: false, canUndoFinish: false };
    }
    
    // Legacy job_status handling
    const statusMap = {
      in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-800", canComplete: true, canFinish: false, canUndoFinish: false },
      contractor_complete: { label: "Awaiting Confirmation", color: "bg-yellow-100 text-yellow-800", canComplete: false, canFinish: false, canUndoFinish: false },
      verified: { label: "Completed", color: "bg-green-100 text-green-800", canComplete: false, canFinish: false, canUndoFinish: false },
      disputed: { label: "Disputed", color: "bg-red-100 text-red-800", canComplete: false, canFinish: false, canUndoFinish: false }
    };
    return statusMap[jobStatus] || { label: "New", color: "bg-gray-100 text-gray-800", canComplete: false, canFinish: false, canUndoFinish: false };
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const isSubscribed = subscription?.subscription_status === 'active';
  const creditBalance = subscription?.is_unlimited_credits ? '∞' : (subscription?.credit_balance || 0);
  const currentPlan = subscription?.subscription_plan;
  const isVerifiedPro = subscription?.verified_pro || profile?.verified_pro || false;
  const profileCompleteness = subscription?.profile_completeness || profile?.profile_completeness || 0;

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-gray-50" data-testid="contractor-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#0FA3B1] flex items-center justify-center">
            <Home className="w-4 h-4 text-white" />
          </div>
          <span className="font-manrope font-bold text-[#1F2933]">Contractor Hub LLC</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-white">Contractor Hub LLC</span>
          </Link>

          <div className="mb-8">
            <div className="flex items-center gap-3 p-3 bg-white/10 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-[#0FA3B1] flex items-center justify-center relative">
                <Briefcase className="w-5 h-5 text-white" />
                {isVerifiedPro && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#7ED957] rounded-full flex items-center justify-center">
                    <BadgeCheck className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
              <div>
                <p className="font-medium text-white text-sm">{profile?.business_name || user?.name}</p>
                <p className="text-white/60 text-xs">Contractor</p>
              </div>
            </div>
            
            {/* Credit Balance Display */}
            <div className="mt-3 px-3 py-2 bg-white/5 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-xs">Credits</span>
                <span className="text-white font-bold">{creditBalance}</span>
              </div>
            </div>
            
            {/* Verified Pro Badge in Sidebar */}
            {isVerifiedPro && (
              <div className="mt-2 px-3 py-2 bg-[#7ED957]/30 rounded-lg">
                <p className="text-[#7ED957] text-xs font-medium flex items-center gap-1">
                  <BadgeCheck className="w-3 h-3" /> Verified Pro
                </p>
              </div>
            )}
            
            {/* Contractor Badges */}
            {profile?.badges_earned && profile.badges_earned.length > 0 && (
              <div className="mt-2 px-3 py-2 bg-white/10 rounded-lg">
                <p className="text-white/60 text-xs mb-2">Badges</p>
                <BadgeRow badges={profile.badges_earned} maxBadges={3} size="sm" showLabels={false} />
              </div>
            )}
            
            {isSubscribed && currentPlan && (
              <div className="mt-2 px-3 py-2 bg-[#7ED957]/20 rounded-lg">
                <p className="text-[#7ED957] text-xs font-medium flex items-center gap-1">
                  <Check className="w-3 h-3" /> {subscriptionPlans[currentPlan]?.name || currentPlan} Plan
                </p>
              </div>
            )}
          </div>

          <nav className="space-y-1">
            <button 
              onClick={() => { setActiveTab("leads"); setSidebarOpen(false); }}
              className={`sidebar-link w-full text-left ${activeTab === 'leads' ? 'active' : ''}`}
              data-testid="nav-available-leads"
            >
              <Eye className="w-5 h-5" /> Available Leads
              {availableLeads.length > 0 && (
                <span className="ml-auto bg-[#0FA3B1] text-white text-xs px-2 py-0.5 rounded-full">
                  {availableLeads.length}
                </span>
              )}
            </button>
            <button 
              onClick={() => { setActiveTab("my-leads"); setSidebarOpen(false); }}
              className={`sidebar-link w-full text-left ${activeTab === 'my-leads' ? 'active' : ''}`}
              data-testid="nav-my-leads"
            >
              <Unlock className="w-5 h-5" /> My Leads
              {myLeads.filter(l => !["lost", "completed", "completed_verified", "contractor_marked_complete"].includes(l.unlock_status) && l.job_status !== "completed_verified").length > 0 && (
                <span className="ml-auto bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {myLeads.filter(l => !["lost", "completed", "completed_verified", "contractor_marked_complete"].includes(l.unlock_status) && l.job_status !== "completed_verified").length}
                </span>
              )}
            </button>
            <button 
              onClick={() => { setActiveTab("subscription"); setSidebarOpen(false); }}
              className={`sidebar-link w-full text-left ${activeTab === 'subscription' ? 'active' : ''}`}
              data-testid="nav-subscription"
            >
              <CreditCard className="w-5 h-5" /> Subscription
            </button>
            <button 
              onClick={() => { setActiveTab("ratings"); setSidebarOpen(false); }}
              className={`sidebar-link w-full text-left ${activeTab === 'ratings' ? 'active' : ''}`}
              data-testid="nav-ratings"
            >
              <BarChart3 className="w-5 h-5" /> Ratings & Performance
            </button>
            <button 
              onClick={() => { navigate("/contractor/profile/edit"); setSidebarOpen(false); }}
              className="sidebar-link w-full text-left"
              data-testid="nav-profile"
            >
              <User className="w-5 h-5" /> Profile
            </button>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6">
          <button 
            onClick={handleLogout}
            className="sidebar-link w-full text-left text-red-300 hover:bg-red-500/20"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-[260px] p-6 lg:p-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {/* Subscription Status Banner - Shows on all tabs */}
            <SubscriptionStatusBanner 
              subscriptionData={subscriptionStatus}
              onUpgrade={() => setActiveTab("subscription")}
              onStartTrial={handleStartTrial}
            />
            
            {/* Available Leads Tab */}
            {activeTab === "leads" && (
              <div>
                <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h1 className="font-manrope font-bold text-2xl lg:text-3xl text-[#1F2933]">
                      Available Leads
                    </h1>
                    <p className="text-[#1F2933]/60">
                      Unlock leads to get homeowner contact info
                    </p>
                  </div>
                  <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-gray-100">
                    <Coins className="w-5 h-5 text-amber-500" />
                    <span className="text-[#1F2933]/60 text-sm">Credits:</span>
                    <span className="font-bold text-[#1F2933]">{creditBalance}</span>
                  </div>
                </div>

                {!isSubscribed && !subscriptionStatus?.can_access_leads && (
                  <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-xl flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-purple-800 font-medium">
                        {subscriptionStatus?.trial_used ? "Trial Ended" : "Start Your Pro Trial"}
                      </p>
                      <p className="text-purple-700 text-sm mt-1">
                        {subscriptionStatus?.trial_used 
                          ? "Your Pro trial has ended. Subscribe to continue accessing leads."
                          : "Get 7 days of Pro access with 25 credits. No credit card required."}
                      </p>
                      <div className="flex gap-2 mt-3">
                        {!subscriptionStatus?.trial_used && (
                          <Button 
                            size="sm"
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                            onClick={handleStartTrial}
                            data-testid="start-free-trial-btn"
                          >
                            <Gift className="w-4 h-4 mr-1" /> Start Pro Trial
                          </Button>
                        )}
                        <Button 
                          size="sm"
                          variant={subscriptionStatus?.trial_used ? "default" : "outline"}
                          className={subscriptionStatus?.trial_used 
                            ? "bg-[#0FA3B1] hover:bg-[#0D8C98] text-white"
                            : "border-purple-500 text-purple-700 hover:bg-purple-100"}
                          onClick={() => setActiveTab("subscription")}
                        >
                          View Plans <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Verified Pro Status Card */}
                {(isSubscribed || subscriptionStatus?.can_access_leads) && (
                  isVerifiedPro ? (
                    <div className="mb-6 p-4 bg-[#7ED957]/10 border border-[#7ED957]/30 rounded-xl flex items-start gap-3" data-testid="verified-pro-badge-card">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="w-10 h-10 rounded-full bg-[#7ED957] flex items-center justify-center cursor-help flex-shrink-0">
                            <BadgeCheck className="w-5 h-5 text-white" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-sm">Verified Pro: Insured contractor with verified online reviews</p>
                        </TooltipContent>
                      </Tooltip>
                      <div>
                        <div className="text-[#1F2933] font-medium flex items-center gap-2">
                          Verified Pro Status
                          <Badge className="bg-[#7ED957]/20 text-[#2D5A27] text-xs">Active</Badge>
                        </div>
                        <p className="text-[#1F2933]/60 text-sm mt-1">
                          Your leads are boosted in placement. Insured contractor with verified online reviews.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3" data-testid="earn-verified-pro-card">
                      <TrendingUp className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-blue-800 font-medium">Earn Verified Pro Badge</p>
                        <p className="text-blue-700 text-sm mt-1">
                          Add insurance + a review link to boost your placement and win more jobs.
                        </p>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="mt-3 border-blue-300 text-blue-700 hover:bg-blue-100"
                          onClick={() => navigate("/contractor/profile/edit")}
                        >
                          Update Profile <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )
                )}

                {/* Lead Filters - Only show when subscribed */}
                {(isSubscribed || subscriptionStatus?.can_access_leads) && (
                  <LeadsFilterBar
                    onFilterChange={handleFilterChange}
                    profileDefaults={filterPreferences.profile_defaults}
                    savedPreferences={filterPreferences.saved_preferences}
                    token={token}
                    API={API}
                  />
                )}

                {/* Loading state for leads */}
                {leadsLoading ? (
                  <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
                    <div className="spinner w-8 h-8 border-2 border-[#0FA3B1]/20 border-t-[#0FA3B1] rounded-full mx-auto mb-4"></div>
                    <p className="text-[#1F2933]/60">Loading leads...</p>
                  </div>
                ) : availableLeads.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
                    <Eye className="w-12 h-12 text-[#1F2933]/20 mx-auto mb-4" />
                    <p className="text-[#1F2933]/60">
                      {currentFilters.job_sizes?.length > 0 || currentFilters.job_types?.length > 0
                        ? "No leads match your current filters."
                        : "No leads available matching your skills yet."}
                    </p>
                    <p className="text-[#1F2933]/40 text-sm mt-2">
                      {currentFilters.job_sizes?.length > 0 || currentFilters.job_types?.length > 0
                        ? "Try adjusting your filters or check back later."
                        : "Check back soon for new opportunities!"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {availableLeads.map((lead) => (
                      <div 
                        key={lead.lead_id}
                        className={`bg-white rounded-2xl p-6 border shadow-sm hover:shadow-md transition-shadow ${
                          lead.is_upgraded 
                            ? 'border-amber-300 ring-1 ring-amber-200' 
                            : 'border-gray-100'
                        }`}
                        data-testid={`lead-${lead.lead_id}`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <h3 className="font-manrope font-bold text-lg text-[#1F2933]">
                                {lead.job_title || lead.category_name}
                              </h3>
                              <Badge className="bg-[#0FA3B1]/10 text-[#0FA3B1] text-xs">
                                {lead.job_budget_tier}
                              </Badge>
                              {/* Upgraded Lead Badge with Tooltip */}
                              {lead.is_upgraded && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge 
                                      className="bg-amber-100 text-amber-800 text-xs cursor-help flex items-center gap-1"
                                      data-testid={`upgraded-lead-badge-${lead.lead_id}`}
                                    >
                                      <Zap className="w-3 h-3" /> Upgraded Lead
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p className="font-medium mb-1">Why is this lead upgraded?</p>
                                    <ul className="text-xs space-y-1">
                                      {lead.upgrade_reasons?.map((reason, idx) => (
                                        <li key={idx}>• {reason}</li>
                                      ))}
                                    </ul>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {/* Demo Lead Badge */}
                              {lead.is_demo && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge className="bg-purple-100 text-purple-700 text-xs cursor-help">
                                      <Beaker className="w-3 h-3 mr-1" /> Demo Job
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p className="text-xs">This is a test job for demo purposes. You can practice the workflow without affecting real homeowners or billing.</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {/* Insurance Required Badge */}
                              {lead.insurance_required && !profile?.is_insured && (
                                <Badge className="bg-red-100 text-red-700 text-xs">
                                  <Shield className="w-3 h-3 mr-1" /> Insurance Required
                                </Badge>
                              )}
                            </div>
                            
                            <div className="grid sm:grid-cols-2 gap-3 text-sm text-[#1F2933]/70">
                              <p className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" /> {lead.location_zip || "Austin Area"}
                              </p>
                              <p className="flex items-center gap-2">
                                <DollarSign className="w-4 h-4" /> 
                                {formatPrice(lead.estimated_min)} - {formatPrice(lead.estimated_max)}
                              </p>
                              <p className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" /> {lead.urgency || "Flexible"}
                              </p>
                              <p className="flex items-center gap-2">
                                <User className="w-4 h-4" /> {lead.available_slots} spot{lead.available_slots !== 1 ? 's' : ''} left
                              </p>
                            </div>
                            
                            {lead.job_details && (
                              <p className="mt-3 text-sm text-[#1F2933]/60 line-clamp-2">
                                {lead.job_details}
                              </p>
                            )}
                            
                            {/* Unlisted-service description (customer's "Can't find" write-in) */}
                            {lead.answers?.unlisted_service_description && (
                              <div className="mt-3 p-3 bg-[#0FA3B1]/10 rounded-lg border border-[#0FA3B1]/30" data-testid="lead-unlisted-desc">
                                <p className="text-xs font-medium text-[#0FA3B1] mb-1">Custom Request:</p>
                                <p className="text-sm text-[#1F2933] line-clamp-3">
                                  {lead.answers.unlisted_service_description}
                                </p>
                              </div>
                            )}
                            
                            {/* Additional Details from homeowner */}
                            {lead.answers?.additional_details && (
                              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid="lead-additional-details">
                                <p className="text-xs font-medium text-blue-700 mb-1">Additional Details from Homeowner:</p>
                                <p className="text-sm text-blue-800 line-clamp-3">
                                  {lead.answers.additional_details}
                                </p>
                              </div>
                            )}
                            
                            {/* Project photo count (photos visible after unlocking) */}
                            {lead.photo_count > 0 && (
                              <p className="mt-2 text-xs text-[#1F2933]/60 flex items-center gap-1" data-testid={`lead-photo-count-${lead.lead_id}`}>
                                <Camera className="w-3.5 h-3.5" />
                                {lead.photo_count} project photo{lead.photo_count !== 1 ? 's' : ''} attached — unlock to view
                              </p>
                            )}
                            
                            {/* Labor-Only Badge - REMOVED from contractor side per user request */}
                          </div>
                          
                          <div className="flex flex-col items-end gap-2">
                            <div className="text-right">
                              <p className="text-xs text-[#1F2933]/50">Unlock Cost</p>
                              {lead.is_upgraded ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <p className="font-bold text-lg text-amber-600 flex items-center gap-1 cursor-help" data-testid={`credit-cost-${lead.lead_id}`}>
                                      <Coins className="w-4 h-4" /> {lead.credit_cost} credits
                                      <Info className="w-3 h-3 text-amber-500" />
                                    </p>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p className="text-xs">Higher credit cost due to project complexity. Upgraded leads represent higher-value opportunities.</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <p className="font-bold text-lg text-[#0FA3B1] flex items-center gap-1" data-testid={`credit-cost-${lead.lead_id}`}>
                                  <Coins className="w-4 h-4" /> {lead.credit_cost} credits
                                </p>
                              )}
                            </div>
                            {/* Verified Pro Boosted Placement Label */}
                            {isVerifiedPro && (
                              <p className="text-xs text-[#7ED957] flex items-center gap-1" data-testid="verified-pro-boost-label">
                                <BadgeCheck className="w-3 h-3" /> Verified Pro boosted placement
                              </p>
                            )}
                            <Button
                              onClick={() => openUnlockDialog(lead)}
                              disabled={!isSubscribed || (!subscription?.is_unlimited_credits && creditBalance < lead.credit_cost) || (lead.insurance_required && !profile?.is_insured)}
                              className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white"
                              data-testid={`unlock-btn-${lead.lead_id}`}
                            >
                              <Unlock className="w-4 h-4 mr-2" /> Unlock Lead
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* My Leads Tab */}
            {activeTab === "my-leads" && (
              <div>
                <div className="mb-6">
                  <h1 className="font-manrope font-bold text-2xl lg:text-3xl text-[#1F2933]">
                    My Leads
                  </h1>
                  <p className="text-[#1F2933]/60">
                    Leads you&apos;ve unlocked with full contact information
                  </p>
                </div>

                {/* Sub-tabs for Active/Archived */}
                <div className="flex gap-2 mb-6">
                  <Button
                    variant={leadsSubTab === "active" ? "default" : "outline"}
                    onClick={() => setLeadsSubTab("active")}
                    className={leadsSubTab === "active" ? "bg-[#0FA3B1] hover:bg-[#0D8C98]" : ""}
                    data-testid="leads-tab-active"
                  >
                    Active Leads
                    {myLeads.filter(l => !["lost", "completed", "completed_verified", "contractor_marked_complete"].includes(l.unlock_status) && l.job_status !== "completed_verified").length > 0 && (
                      <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                        {myLeads.filter(l => !["lost", "completed", "completed_verified", "contractor_marked_complete"].includes(l.unlock_status) && l.job_status !== "completed_verified").length}
                      </span>
                    )}
                  </Button>
                  <Button
                    variant={leadsSubTab === "archived" ? "default" : "outline"}
                    onClick={() => setLeadsSubTab("archived")}
                    className={leadsSubTab === "archived" ? "bg-gray-600 hover:bg-gray-700" : ""}
                    data-testid="leads-tab-archived"
                  >
                    Archived
                    {myLeads.filter(l => ["lost", "completed", "completed_verified", "contractor_marked_complete"].includes(l.unlock_status) || l.job_status === "completed_verified").length > 0 && (
                      <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                        {myLeads.filter(l => ["lost", "completed", "completed_verified", "contractor_marked_complete"].includes(l.unlock_status) || l.job_status === "completed_verified").length}
                      </span>
                    )}
                  </Button>
                </div>

                {/* Filter leads based on sub-tab */}
                {(() => {
                  const isArchived = (lead) => 
                    ["lost", "completed", "completed_verified", "contractor_marked_complete"].includes(lead.unlock_status) || 
                    lead.job_status === "completed_verified";
                  
                  const filteredLeads = leadsSubTab === "active" 
                    ? myLeads.filter(l => !isArchived(l))
                    : myLeads.filter(l => isArchived(l));

                  if (filteredLeads.length === 0) {
                    return (
                      <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
                        {leadsSubTab === "active" ? (
                          <>
                            <Unlock className="w-12 h-12 text-[#1F2933]/20 mx-auto mb-4" />
                            <p className="text-[#1F2933]/60">No active leads.</p>
                            <Button 
                              variant="link" 
                              className="text-[#0FA3B1] mt-2"
                              onClick={() => setActiveTab("leads")}
                            >
                              Browse available leads
                            </Button>
                          </>
                        ) : (
                          <>
                            <Archive className="w-12 h-12 text-[#1F2933]/20 mx-auto mb-4" />
                            <p className="text-[#1F2933]/60">No archived leads yet.</p>
                            <p className="text-[#1F2933]/40 text-sm mt-1">
                              Completed and lost leads will appear here.
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-4">
                      {filteredLeads.map((lead) => (
                      <div 
                        key={lead.lead_id}
                        className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
                        data-testid={`my-lead-${lead.lead_id}`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-manrope font-bold text-lg text-[#1F2933]">
                                {lead.category_name}
                              </h3>
                              <Badge className={`text-xs ${getUnlockBadgeClass(lead.unlock_status)}`}>
                                {getUnlockLabel(lead.unlock_status)}
                              </Badge>
                            </div>
                            
                            {/* Contact Info - Visible for unlocked leads */}
                            <div className="bg-[#7ED957]/10 rounded-xl p-4 mb-4">
                              <p className="text-xs text-[#1F2933]/50 mb-2 font-medium uppercase tracking-wide">
                                Contact Information
                              </p>
                              <div className="grid sm:grid-cols-2 gap-3">
                                <p className="flex items-center gap-2 text-[#1F2933]">
                                  <User className="w-4 h-4 text-[#0FA3B1]" /> 
                                  <span className="font-medium">{lead.homeowner_name}</span>
                                </p>
                                <a href={`tel:${lead.homeowner_phone}`} className="flex items-center gap-2 text-[#0FA3B1] hover:underline">
                                  <Phone className="w-4 h-4" /> {lead.homeowner_phone}
                                </a>
                                <a href={`mailto:${lead.homeowner_email}`} className="flex items-center gap-2 text-[#0FA3B1] hover:underline">
                                  <Mail className="w-4 h-4" /> {lead.homeowner_email}
                                </a>
                                <p className="flex items-center gap-2 text-[#1F2933]/70">
                                  <MapPin className="w-4 h-4" /> {lead.homeowner_address}
                                </p>
                              </div>
                            </div>
                            
                            <div className="grid sm:grid-cols-2 gap-3 text-sm text-[#1F2933]/70">
                              <p className="flex items-center gap-2">
                                <DollarSign className="w-4 h-4" /> 
                                Budget: {lead.job_budget_tier}
                              </p>
                              <p className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" /> 
                                Unlocked: {new Date(lead.unlocked_at).toLocaleDateString()}
                              </p>
                            </div>
                            
                            {lead.job_details && (
                              <p className="mt-3 text-sm text-[#1F2933]/60">
                                {lead.job_details}
                              </p>
                            )}
                            
                            {/* Unlisted-service description (customer's "Can't find" write-in) — visible after unlock */}
                            {lead.answers?.unlisted_service_description && (
                              <div className="mt-3 p-3 bg-[#0FA3B1]/10 rounded-lg border border-[#0FA3B1]/30" data-testid="unlocked-unlisted-desc">
                                <p className="text-xs font-medium text-[#0FA3B1] mb-1">Custom Request:</p>
                                <p className="text-sm text-[#1F2933]">
                                  {lead.answers.unlisted_service_description}
                                </p>
                              </div>
                            )}
                            
                            {/* Additional Details from homeowner */}
                            {lead.answers?.additional_details && (
                              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                                <p className="text-xs font-medium text-blue-700 mb-1">Additional Details from Homeowner:</p>
                                <p className="text-sm text-blue-800">
                                  {lead.answers.additional_details}
                                </p>
                              </div>
                            )}
                            
                            {/* Project photos from homeowner */}
                            <div className="mt-3">
                              <LeadPhotoGallery leadId={lead.lead_id} token={token} />
                            </div>
                            
                            {/* Labor-Only Disclaimer - REMOVED from contractor side per user request */}
                          </div>
                          
                          <div className="flex flex-col gap-3">
                            {/* Job Status Badge */}
                            {(() => {
                              const jobInfo = getJobStatusInfo(lead);
                              return (
                                <div className="text-right">
                                  <p className="text-xs text-[#1F2933]/50 mb-1">Job Status</p>
                                  <Badge className={jobInfo.color}>
                                    {jobInfo.label}
                                  </Badge>
                                </div>
                              );
                            })()}
                            
                            {/* Quick Status Updates */}
                            <div className="flex flex-col gap-2">
                              <p className="text-xs text-[#1F2933]/50 text-right">
                                {leadsSubTab === "archived" ? "Actions" : "Quick Status"}
                              </p>
                              <div className="flex gap-1 flex-wrap justify-end">
                                {leadsSubTab === "archived" ? (
                                  /* Archived leads - show Restore/Undo buttons based on status */
                                  lead.unlock_status === "completed" ? (
                                    /* Completed leads - show Undo Finished if not rated yet */
                                    getJobStatusInfo(lead).canUndoFinish && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleUndoFinish(lead)}
                                        className="border-amber-500 text-amber-600 hover:bg-amber-50"
                                        data-testid={`undo-finish-${lead.lead_id}`}
                                      >
                                        <ArrowRight className="w-3 h-3 mr-1 rotate-180" />
                                        Undo Finished
                                      </Button>
                                    )
                                  ) : (
                                    /* Other archived leads - show Restore button */
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => updateLeadStatus(lead.lead_id, 'new')}
                                      className="border-[#0FA3B1] text-[#0FA3B1] hover:bg-[#0FA3B1]/10"
                                    >
                                      <ArrowRight className="w-3 h-3 mr-1 rotate-180" />
                                      Restore to Active
                                    </Button>
                                  )
                                ) : (
                                  /* Active leads - show Contracted/Lost/Finished buttons */
                                  <>
                                    <Button
                                      size="sm"
                                      variant={lead.unlock_status === 'contracted' ? 'default' : 'outline'}
                                      onClick={() => updateLeadStatus(lead.lead_id, 'contracted')}
                                      className={lead.unlock_status === 'contracted' ? 'bg-blue-500' : ''}
                                      data-testid={`contracted-btn-${lead.lead_id}`}
                                    >
                                      Contracted
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant={lead.unlock_status === 'lost' ? 'default' : 'outline'}
                                      onClick={() => updateLeadStatus(lead.lead_id, 'lost')}
                                      className={lead.unlock_status === 'lost' ? 'bg-red-500' : ''}
                                      data-testid={`lost-btn-${lead.lead_id}`}
                                    >
                                      Lost
                                    </Button>
                                    {/* Finished Button - Only enabled when Contracted */}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleFinishJob(lead)}
                                      disabled={lead.unlock_status !== 'contracted'}
                                      className={
                                        lead.unlock_status === 'contracted'
                                          ? 'border-green-500 text-green-600 hover:bg-green-50'
                                          : 'border-gray-200 text-gray-400 cursor-not-allowed'
                                      }
                                      data-testid={`finished-btn-${lead.lead_id}`}
                                    >
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Finished
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  );
                })()}
              </div>
            )}

            {/* Subscription Tab */}
            {activeTab === "subscription" && (
              <div>
                <div className="mb-8">
                  <h1 className="font-manrope font-bold text-2xl lg:text-3xl text-[#1F2933]">
                    Subscription Plans
                  </h1>
                  <p className="text-[#1F2933]/60">
                    Choose a plan to unlock leads and grow your business
                  </p>
                </div>

                {/* Current Status */}
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm mb-8">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h2 className="font-manrope font-bold text-lg text-[#1F2933]">Your Status</h2>
                      {isSubscribed ? (
                        <div className="flex items-center gap-2 mt-1">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span className="text-green-700 font-medium">
                            Active - {subscriptionPlans[currentPlan]?.name || currentPlan} Plan
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mt-1">
                          <Lock className="w-5 h-5 text-gray-400" />
                          <span className="text-[#1F2933]/60">No active subscription</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-[#1F2933]/50">Credit Balance</p>
                      <p className="text-3xl font-bold text-[#0FA3B1] flex items-center justify-end gap-2">
                        <Coins className="w-6 h-6" /> {creditBalance}
                      </p>
                      <p className="text-xs text-[#1F2933]/40 mt-1">
                        Credits roll over for up to 60 days
                      </p>
                    </div>
                  </div>
                  
                  {isSubscribed && subscription?.subscription_end && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-sm text-[#1F2933]/60">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Renews: {new Date(subscription.subscription_end).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Credit Cost Info */}
                <div className="bg-[#0FA3B1]/5 rounded-2xl p-6 border border-[#0FA3B1]/20 mb-8">
                  <h3 className="font-manrope font-bold text-lg text-[#1F2933] mb-4">Credit Cost per Lead</h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
                    <div className="bg-white rounded-xl p-4 text-center">
                      <p className="text-sm text-[#1F2933]/60 mb-1">Less than $1,000</p>
                      <p className="text-2xl font-bold text-[#0FA3B1]">2 credits</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center">
                      <p className="text-sm text-[#1F2933]/60 mb-1">$1,000 – $5,000</p>
                      <p className="text-2xl font-bold text-[#0FA3B1]">4 credits</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center">
                      <p className="text-sm text-[#1F2933]/60 mb-1">$5,000 – $10,000</p>
                      <p className="text-2xl font-bold text-[#0FA3B1]">6 credits</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center">
                      <p className="text-sm text-[#1F2933]/60 mb-1">$10,000 – $15,000</p>
                      <p className="text-2xl font-bold text-[#0FA3B1]">10 credits</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center">
                      <p className="text-sm text-[#1F2933]/60 mb-1">$15,000 – $25,000</p>
                      <p className="text-2xl font-bold text-[#0FA3B1]">15 credits</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center">
                      <p className="text-sm text-[#1F2933]/60 mb-1">$25,000+</p>
                      <p className="text-2xl font-bold text-[#0FA3B1]">25 credits</p>
                    </div>
                  </div>
                </div>

                {/* Credit Top-Up Section (only for subscribers) */}
                {isSubscribed && (
                  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-manrope font-bold text-lg text-[#1F2933]">Need More Credits?</h3>
                        <p className="text-sm text-[#1F2933]/60">Purchase additional credits anytime</p>
                      </div>
                      <Package className="w-8 h-8 text-[#0FA3B1]" />
                    </div>
                    
                    <div className="grid sm:grid-cols-3 gap-4">
                      {Object.entries(creditPacks).map(([packId, pack]) => {
                        const style = PACK_STYLES[packId] || PACK_STYLES.pack_10;
                        const pricePerCredit = (pack.price / pack.credits).toFixed(2);
                        
                        return (
                          <div 
                            key={packId}
                            className={`border rounded-xl p-4 relative ${style.popular ? 'border-blue-500 border-2' : 'border-gray-200'}`}
                          >
                            {style.popular && (
                              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                                POPULAR
                              </div>
                            )}
                            
                            <div className="text-center">
                              <p className="text-3xl font-bold text-[#1F2933]">{pack.credits}</p>
                              <p className="text-sm text-[#1F2933]/60 mb-2">credits</p>
                              <p className="text-xl font-bold text-[#0FA3B1] mb-1">${pack.price}</p>
                              <p className="text-xs text-[#1F2933]/40 mb-3">${pricePerCredit}/credit</p>
                              
                              <Button
                                size="sm"
                                onClick={() => handleBuyCreditPack(packId)}
                                disabled={purchasingPack === packId}
                                className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white"
                              >
                                {purchasingPack === packId ? (
                                  <div className="spinner w-4 h-4 border-2 border-white/20 border-t-white"></div>
                                ) : (
                                  <>
                                    <Plus className="w-4 h-4 mr-1" /> Buy Now
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <p className="text-xs text-[#1F2933]/40 mt-4 text-center">
                      Credits expire using the same 60-day rollover rule as your subscription credits
                    </p>
                  </div>
                )}

                {/* Plans Grid */}
                {/* Pro Trial CTA - Show only if trial not used and not subscribed */}
                {!subscriptionStatus?.trial_used && !isSubscribed && (
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-2xl p-6 mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-xl bg-purple-500 flex items-center justify-center flex-shrink-0">
                          <Gift className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="font-manrope font-bold text-xl text-purple-900">
                            Try Pro Free for 7 Days
                          </h3>
                          <p className="text-purple-700 mt-1">
                            Get full Pro access with 25 credits. Access leads up to $25,000. No credit card required.
                          </p>
                          <div className="flex flex-wrap gap-3 mt-2 text-sm text-purple-600">
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" /> 7 days Pro access
                            </span>
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" /> 25 credits included
                            </span>
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" /> Leads up to $25,000
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={handleStartTrial}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 text-lg flex-shrink-0"
                        data-testid="start-pro-trial-btn"
                      >
                        <Star className="w-5 h-5 mr-2" /> Start Pro Trial
                      </Button>
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-3 gap-6">
                  {['starter', 'pro', 'elite']
                    .filter(key => subscriptionPlans[key])
                    .map((key) => {
                    const plan = subscriptionPlans[key];
                    const style = PLAN_STYLES[key] || PLAN_STYLES.starter;
                    const Icon = style.icon;
                    const isCurrentPlan = currentPlan === key;
                    const isPro = key === 'pro';
                    const costPerCredit = plan.credits > 0 ? (plan.price / plan.credits).toFixed(2) : 0;
                    
                    return (
                      <div 
                        key={key}
                        className={`bg-white rounded-2xl p-6 border-2 transition-all relative ${
                          isCurrentPlan 
                            ? 'border-[#0FA3B1] shadow-lg shadow-[#0FA3B1]/10' 
                            : isPro
                              ? 'border-purple-500 shadow-lg shadow-purple-500/10'
                              : 'border-gray-100 hover:border-gray-200'
                        } ${isPro ? 'md:-mt-4 md:pb-10' : ''}`}
                        data-testid={`plan-${key}`}
                      >
                        {isPro && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500 text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap">
                            MOST POPULAR
                          </div>
                        )}
                        
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`w-12 h-12 rounded-xl ${style.color} flex items-center justify-center`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-manrope font-bold text-xl text-[#1F2933]">{plan.name}</h3>
                            {isCurrentPlan && (
                              <Badge className="bg-[#0FA3B1]/10 text-[#0FA3B1] text-xs">Current Plan</Badge>
                            )}
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <span className="text-4xl font-bold text-[#1F2933]">${plan.price}</span>
                          <span className="text-[#1F2933]/50"> / month</span>
                        </div>
                        
                        <div className={`rounded-xl p-4 mb-4 ${isPro ? 'bg-purple-50' : 'bg-gray-50'}`}>
                          <p className="text-sm text-[#1F2933]/60 mb-1">Monthly Credits</p>
                          <p className={`text-2xl font-bold flex items-center gap-2 ${isPro ? 'text-purple-600' : 'text-[#0FA3B1]'}`}>
                            <Coins className="w-5 h-5" /> {plan.credits} credits
                          </p>
                          <p className="text-xs text-[#1F2933]/50 mt-1">${costPerCredit} / credit</p>
                        </div>
                        
                        <ul className="space-y-2.5 mb-4">
                          {plan.features?.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-[#1F2933]/70">
                              <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isPro ? 'text-purple-500' : 'text-[#7ED957]'}`} />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        
                        {/* Helper text / description */}
                        {plan.description && (
                          <p className="text-xs text-[#1F2933]/50 mb-4 italic">
                            {plan.description}
                          </p>
                        )}
                        
                        <Button
                          onClick={() => handleSubscribe(key)}
                          disabled={subscribingPlan === key}
                          className={`w-full ${
                            isCurrentPlan 
                              ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                              : isPro
                                ? 'bg-purple-500 hover:bg-purple-600 text-white'
                                : 'bg-[#0FA3B1] hover:bg-[#0D8C98] text-white'
                          }`}
                          data-testid={`subscribe-${key}`}
                        >
                          {subscribingPlan === key ? (
                            <div className="spinner w-5 h-5 border-2 border-white/20 border-t-white"></div>
                          ) : isCurrentPlan ? (
                            "Current Plan"
                          ) : isSubscribed ? (
                            "Switch Plan"
                          ) : (
                            "Subscribe"
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === "profile" && (
              <div>
                <div className="mb-8">
                  <h1 className="font-manrope font-bold text-2xl lg:text-3xl text-[#1F2933]">
                    Business Profile
                  </h1>
                  <p className="text-[#1F2933]/60">
                    Your contractor profile summary
                  </p>
                </div>

                <div className="max-w-3xl space-y-6">
                  {/* Profile Completeness Card */}
                  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="profile-completeness-card">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-manrope font-bold text-lg text-[#1F2933]">Profile Completeness</h3>
                      <span className="text-2xl font-bold text-[#0FA3B1]">{profileCompleteness}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                      <div 
                        className="h-3 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${profileCompleteness}%`,
                          backgroundColor: getCompletenessColor(profileCompleteness)
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#1F2933]/60">
                        {getCompletenessText(profileCompleteness)}
                      </span>
                      {profileCompleteness < 100 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate("/contractor/profile/edit")}
                          className="text-[#0FA3B1]"
                        >
                          Complete Profile
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Verified Pro Status Card */}
                  <div className={`rounded-2xl p-6 border shadow-sm ${isVerifiedPro ? 'bg-[#7ED957]/5 border-[#7ED957]/30' : 'bg-gray-50 border-gray-100'}`} data-testid="verified-pro-status-card">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${isVerifiedPro ? 'bg-[#7ED957]' : 'bg-gray-200'}`}>
                        <BadgeCheck className={`w-7 h-7 ${isVerifiedPro ? 'text-white' : 'text-gray-400'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-manrope font-bold text-lg text-[#1F2933]">Verified Pro</h3>
                          {isVerifiedPro ? (
                            <Badge className="bg-[#7ED957]/20 text-[#2D5A27]">Active</Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-500">Not Earned</Badge>
                          )}
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <p className="text-sm text-[#1F2933]/60 flex items-center gap-1 cursor-help">
                              <Info className="w-3 h-3" />
                              {isVerifiedPro 
                                ? 'Insured contractor with verified online reviews'
                                : 'Add insurance + a review link to earn this badge'}
                            </p>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-sm max-w-xs">
                              Verified Pro: Insured contractor with verified online reviews. 
                              This badge boosts your placement in lead results.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      {!isVerifiedPro && (
                        <Button
                          size="sm"
                          onClick={() => navigate("/contractor/profile/edit")}
                          className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white"
                        >
                          Earn Badge
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Profile Summary Card */}
                  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-2xl bg-[#0FA3B1]/10 flex items-center justify-center relative">
                          <Briefcase className="w-8 h-8 text-[#0FA3B1]" />
                          {isVerifiedPro && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#7ED957] rounded-full flex items-center justify-center">
                              <BadgeCheck className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <div>
                          <h2 className="font-manrope font-bold text-xl text-[#1F2933]">
                            {profile?.full_name || profile?.business_name || user?.name}
                          </h2>
                          <p className="text-[#1F2933]/60">{profile?.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="text-sm text-[#1F2933]/60">Contractor Score</p>
                          <p className="text-2xl font-bold text-[#0FA3B1]">{profile?.contractor_score || 0}</p>
                        </div>
                        <Award className="w-8 h-8 text-amber-500" />
                      </div>
                    </div>

                    {/* Status badges */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {isVerifiedPro && (
                        <Badge className="bg-[#7ED957]/20 text-[#2D5A27]">
                          <BadgeCheck className="w-3 h-3 mr-1" /> Verified Pro
                        </Badge>
                      )}
                      {profile?.onboarding_complete && (
                        <Badge className="bg-green-100 text-green-800">
                          <Check className="w-3 h-3 mr-1" /> Profile Complete
                        </Badge>
                      )}
                      {profile?.is_insured && (
                        <Badge className="bg-blue-100 text-blue-800">
                          <Shield className="w-3 h-3 mr-1" /> Insured
                        </Badge>
                      )}
                      {profile?.is_licensed === "Yes" && (
                        <Badge className="bg-purple-100 text-purple-800">
                          <Check className="w-3 h-3 mr-1" /> Licensed
                        </Badge>
                      )}
                      {isSubscribed && (
                        <Badge className="bg-[#7ED957]/20 text-green-800">
                          <Star className="w-3 h-3 mr-1" /> {subscriptionPlans[currentPlan]?.name} Subscriber
                        </Badge>
                      )}
                    </div>

                    {/* Profile details grid */}
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Skills */}
                      <div>
                        <h3 className="font-medium text-[#1F2933] mb-2 flex items-center gap-2">
                          <Wrench className="w-4 h-4 text-[#0FA3B1]" /> Skills & Services
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {profile?.skills?.map(skill => (
                            <Badge key={skill} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Experience */}
                      <div>
                        <h3 className="font-medium text-[#1F2933] mb-2 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-[#0FA3B1]" /> Experience
                        </h3>
                        <p className="text-[#1F2933]/80">{profile?.years_experience || "Not specified"}</p>
                      </div>

                      {/* Service Area */}
                      <div>
                        <h3 className="font-medium text-[#1F2933] mb-2 flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#0FA3B1]" /> Service Area
                        </h3>
                        <p className="text-[#1F2933]/80">{profile?.service_area || "Not specified"}</p>
                      </div>

                      {/* Business Type */}
                      <div>
                        <h3 className="font-medium text-[#1F2933] mb-2 flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-[#0FA3B1]" /> Business Type
                        </h3>
                        <p className="text-[#1F2933]/80">{profile?.business_type || "Not specified"}</p>
                      </div>

                      {/* Job Sizes */}
                      <div>
                        <h3 className="font-medium text-[#1F2933] mb-2 flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-[#0FA3B1]" /> Job Sizes Accepted
                        </h3>
                        <div className="flex flex-wrap gap-1">
                          {profile?.job_size_accepted?.map(size => (
                            <Badge key={size} variant="outline" className="text-xs">
                              {size}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Availability */}
                      <div>
                        <h3 className="font-medium text-[#1F2933] mb-2 flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-[#0FA3B1]" /> Availability
                        </h3>
                        <p className="text-[#1F2933]/80">{profile?.availability || "Not specified"}</p>
                      </div>
                    </div>
                  </div>

                  {/* Update Profile Button */}
                  <Button
                    onClick={() => navigate("/contractor/profile/edit")}
                    variant="outline"
                    className="rounded-full"
                  >
                    Update Profile
                  </Button>
                </div>
              </div>
            )}

            {/* Ratings & Performance Tab */}
            {activeTab === "ratings" && (
              <div>
                <div className="mb-8">
                  <h1 className="font-manrope font-bold text-2xl lg:text-3xl text-[#1F2933]">
                    Ratings & Performance
                  </h1>
                  <p className="text-[#1F2933]/60">
                    Your ratings, profile completeness, and performance tips
                  </p>
                </div>
                <ContractorRatingsSection />
              </div>
            )}
          </>
        )}
      </main>

      {/* Unlock Lead Dialog */}
      <Dialog open={showUnlockDialog} onOpenChange={setShowUnlockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unlock This Lead?</DialogTitle>
            <DialogDescription>
              Spend credits to get the homeowner&apos;s contact information
            </DialogDescription>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4 pt-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-bold text-[#1F2933]">{selectedLead.category_name}</h3>
                  {selectedLead.is_upgraded && (
                    <Badge className="bg-amber-100 text-amber-800 text-xs flex items-center gap-1">
                      <Zap className="w-3 h-3" /> Upgraded
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-[#1F2933]/70 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {selectedLead.location_zip || "Austin Area"}
                </p>
                <p className="text-sm text-[#1F2933]/70 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> {selectedLead.job_budget_tier}
                </p>
              </div>
              
              {/* Upgrade Reasons */}
              {selectedLead.is_upgraded && selectedLead.upgrade_reasons?.length > 0 && (
                <div className="p-3 bg-amber-50/50 rounded-lg border border-amber-100">
                  <p className="text-xs font-medium text-amber-800 mb-1">Why this lead costs more:</p>
                  <ul className="text-xs text-amber-700 space-y-0.5">
                    {selectedLead.upgrade_reasons.map((reason) => (
                      <li key={reason}>• {reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className={`flex items-center justify-between p-4 rounded-xl border ${
                selectedLead.is_upgraded 
                  ? 'bg-amber-50 border-amber-200' 
                  : 'bg-[#0FA3B1]/5 border-[#0FA3B1]/20'
              }`}>
                <div>
                  <p className={`text-sm ${selectedLead.is_upgraded ? 'text-amber-800' : 'text-[#0FA3B1]'}`}>Credit Cost</p>
                  <p className={`text-2xl font-bold flex items-center gap-1 ${
                    selectedLead.is_upgraded ? 'text-amber-700' : 'text-[#0FA3B1]'
                  }`}>
                    <Coins className="w-5 h-5" /> {selectedLead.credit_cost} credits
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#1F2933]/50">Your Balance</p>
                  <p className="text-xl font-bold text-[#1F2933]">{creditBalance} credits</p>
                </div>
              </div>
              
              {/* Insurance Warning */}
              {selectedLead.insurance_required && !profile?.is_insured && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200 flex items-start gap-2">
                  <Shield className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">
                    This lead requires insurance. Update your profile to unlock.
                  </p>
                </div>
              )}
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowUnlockDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[#0FA3B1] hover:bg-[#0D8C98]"
                  onClick={handleUnlockLead}
                  disabled={unlockingLead || (selectedLead.insurance_required && !profile?.is_insured)}
                  data-testid="confirm-unlock-btn"
                >
                  {unlockingLead ? (
                    <div className="spinner w-5 h-5 border-2 border-white/20 border-t-white"></div>
                  ) : (
                    <>
                      <Unlock className="w-4 h-4 mr-2" /> Unlock Lead
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark Job Complete Confirmation Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-[#7ED957]" />
              Confirm job is complete?
            </DialogTitle>
            <DialogDescription>
              Only mark complete when all agreed work is finished. The homeowner will receive an SMS to confirm.
            </DialogDescription>
          </DialogHeader>
          
          {selectedJobLead && (
            <div className="space-y-4 mt-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="font-medium text-[#1F2933]">{selectedJobLead.category_name}</p>
                <p className="text-sm text-[#1F2933]/60">
                  {selectedJobLead.homeowner_name} • {selectedJobLead.homeowner_address}
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
                <p className="font-medium text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  What happens next:
                </p>
                <ul className="mt-2 text-amber-700 space-y-1 ml-6 list-disc">
                  <li>The homeowner will receive an SMS to confirm</li>
                  <li>They can reply YES to verify or NOT YET if work remains</li>
                  <li>Once verified, they can leave a review</li>
                </ul>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowCompleteDialog(false)}
                  disabled={completingJob}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[#7ED957] hover:bg-[#6BC648]"
                  onClick={confirmMarkJobComplete}
                  disabled={completingJob}
                  data-testid="confirm-mark-complete"
                >
                  {completingJob ? (
                    <div className="spinner w-5 h-5 border-2 border-white/20 border-t-white rounded-full" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" /> Yes, Mark Complete
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Finish Job Confirmation Dialog */}
      <Dialog open={showFinishDialog} onOpenChange={setShowFinishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-500" />
              Mark this job as Finished?
            </DialogTitle>
            <DialogDescription>
              This will archive the lead and text the homeowner to rate you.
            </DialogDescription>
          </DialogHeader>
          
          {selectedJobLead && (
            <div className="space-y-4 mt-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="font-medium text-[#1F2933]">{selectedJobLead.category_name}</p>
                <p className="text-sm text-[#1F2933]/60">
                  {selectedJobLead.homeowner_name} • {selectedJobLead.homeowner_address}
                </p>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm">
                <p className="font-medium text-green-800 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  What happens next:
                </p>
                <ul className="mt-2 text-green-700 space-y-1 ml-6 list-disc">
                  <li>Lead will be moved to <strong>Archived</strong></li>
                  <li>Homeowner will receive an SMS with a rating link</li>
                  <li>A reminder will be sent after 24 hours if not rated</li>
                </ul>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowFinishDialog(false)}
                  disabled={finishingJob}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  onClick={confirmFinishJob}
                  disabled={finishingJob}
                  data-testid="confirm-finish-job"
                >
                  {finishingJob ? (
                    <div className="spinner w-5 h-5 border-2 border-white/20 border-t-white rounded-full" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" /> Yes, Mark Finished
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Undo Finish Confirmation Dialog */}
      <Dialog open={showUndoFinishDialog} onOpenChange={setShowUndoFinishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="w-6 h-6 text-amber-500 rotate-180" />
              Undo completion?
            </DialogTitle>
            <DialogDescription>
              This will move the lead back to Active with "Contracted" status.
            </DialogDescription>
          </DialogHeader>
          
          {selectedJobLead && (
            <div className="space-y-4 mt-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="font-medium text-[#1F2933]">{selectedJobLead.category_name}</p>
                <p className="text-sm text-[#1F2933]/60">
                  {selectedJobLead.homeowner_name} • {selectedJobLead.homeowner_address}
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
                <p className="font-medium text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Note:
                </p>
                <ul className="mt-2 text-amber-700 space-y-1 ml-6 list-disc">
                  <li>Lead will return to <strong>Active Leads</strong></li>
                  <li>Status will be set back to "Contracted"</li>
                  <li>A new rating SMS will <strong>NOT</strong> be sent</li>
                </ul>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowUndoFinishDialog(false)}
                  disabled={finishingJob}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={confirmUndoFinish}
                  disabled={finishingJob}
                  data-testid="confirm-undo-finish"
                >
                  {finishingJob ? (
                    <div className="spinner w-5 h-5 border-2 border-white/20 border-t-white rounded-full" />
                  ) : (
                    <>
                      <ArrowRight className="w-4 h-4 mr-2 rotate-180" /> Yes, Undo Completion
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
    </TooltipProvider>
  );
}
